import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { attachProp } from "./props3d";

export const VIEWS = { front: 0, left: 90, right: -90, back: 180, threeQuarter: 35 };

/** Loads a GLB, normalises scale so the soles sit on y=0, plays clips with cross-fade blending, reports real asset stats. */
const Model = ({ url, clip, onReady, onError, prop }) => {
  const [gltf, setGltf] = useState(null);
  const mixer = useRef(null), actions = useRef({}), current = useRef(null);
  useEffect(() => {
    let off = false;
    new GLTFLoader().load(url, (g) => { if (!off) setGltf(g); }, undefined, (e) => onError?.(e));
    return () => { off = true; };
  }, [url, onError]);
  const root = useMemo(() => {
    if (!gltf) return null;
    const s = gltf.scene, box = new THREE.Box3().setFromObject(s), size = box.getSize(new THREE.Vector3()), k = 1.6 / (size.y || 1);
    s.scale.setScalar(k);
    const b2 = new THREE.Box3().setFromObject(s), c = b2.getCenter(new THREE.Vector3());
    s.position.set(s.position.x - c.x, s.position.y - b2.min.y, s.position.z - c.z);
    let tris = 0, bones = 0; const tex = new Set();
    const clay = !(gltf.parser.json.materials || []).length;
    const clayMat = new THREE.MeshPhysicalMaterial({ color: "#D9C4A6", roughness: 0.62, clearcoat: 0.15, clearcoatRoughness: 0.6, sheen: 0.25, sheenColor: new THREE.Color("#F4E3C8") });
    s.traverse((o) => {
      if (clay && o.isMesh) o.material = clayMat;
      if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; tris += (o.geometry.index ? o.geometry.index.count : o.geometry.attributes.position.count) / 3; ["map", "normalMap", "roughnessMap", "metalnessMap"].forEach((m) => o.material?.[m] && tex.add(o.material[m].uuid)); }
      if (o.isBone) bones += 1;
    });
    mixer.current = new THREE.AnimationMixer(s);
    actions.current = Object.fromEntries(gltf.animations.map((a) => [a.name, mixer.current.clipAction(a)]));
    onReady?.({ clips: gltf.animations.map((a) => a.name), triangles: Math.round(tris), bones, textures: tex.size, height: size.y, clay });
    return s;
  }, [gltf, onReady]);
  useEffect(() => {
    if (!root) return;
    if (!clip) {
      mixer.current?.stopAllAction(); current.current = null;
      mixer.current?.update(0);
      return;
    }
    const next = actions.current[clip];
    if (!next || current.current === next) return;
    next.reset().fadeIn(0.35).play();
    current.current?.fadeOut(0.35);
    current.current = next;
  }, [clip, root]);
  useEffect(() => { if (root && prop?.fid) attachProp(root, prop.character, prop.fid, gltf.animations[prop.idle]).catch(() => {}); }, [root, prop, gltf]);
  useFrame((_, dt) => { mixer.current?.update(dt); root?.userData.updateProp?.(); });
  return root ? <primitive object={root} /> : null;
};

const Rig = ({ view, zoom, autoRotate }) => {
  const { camera, gl, size } = useThree();
  const ctl = useRef(null), goal = useRef(null);
  const aspect = size.width / size.height;
  useEffect(() => { const d = aspect < 0.8 ? 4.1 : 3.6; camera.position.setLength(d); }, [aspect, camera]);
  useEffect(() => {
    const c = new OrbitControls(camera, gl.domElement);
    c.target.set(0, 0.78, 0); c.enableDamping = true; c.minDistance = 1.2; c.maxDistance = 6; c.maxPolarAngle = Math.PI * 0.55; c.enablePan = false;
    ctl.current = c;
    return () => c.dispose();
  }, [camera, gl]);
  useEffect(() => { goal.current = THREE.MathUtils.degToRad(VIEWS[view] ?? 0); }, [view]);
  useEffect(() => { if (!ctl.current) return; const d = camera.position.distanceTo(ctl.current.target); const nd = THREE.MathUtils.clamp(d * zoom.k, 1.2, 6), t = ctl.current.target, off = camera.position.clone().sub(t).setLength(nd); t.y = THREE.MathUtils.lerp(0.78, 1.12, THREE.MathUtils.clamp((3.6 - nd) / 2.2, 0, 1)); camera.position.copy(t).add(off); }, [zoom, camera]);
  useFrame(() => {
    const c = ctl.current; if (!c) return;
    c.autoRotate = autoRotate; c.autoRotateSpeed = 1.2;
    if (goal.current != null && !autoRotate) {
      const cur = c.getAzimuthalAngle(); let d = goal.current - cur; d = Math.atan2(Math.sin(d), Math.cos(d));
      if (Math.abs(d) < 0.002) goal.current = null; else { const off = camera.position.clone().sub(c.target); off.applyAxisAngle(new THREE.Vector3(0, 1, 0), d * 0.12); camera.position.copy(c.target).add(off); }
    }
    c.update();
  });
  return null;
};

const Env = () => {
  const { gl, scene } = useThree();
  useEffect(() => { const pm = new THREE.PMREMGenerator(gl), env = pm.fromScene(new RoomEnvironment(), 0.04).texture; scene.environment = env; scene.environmentIntensity = 0.55; return () => { env.dispose(); pm.dispose(); }; }, [gl, scene]);
  return null;
};

const Studio = () => (
  <>
    <Env />
    <color attach="background" args={["#EEF3F1"]} />
    <hemisphereLight args={["#FFF7EA", "#C9D6D2", 0.9]} />
    <directionalLight position={[2.5, 4, 3]} intensity={2.4} castShadow shadow-mapSize={[2048, 2048]} shadow-bias={-0.0004} shadow-camera-left={-2} shadow-camera-right={2} shadow-camera-top={3} shadow-camera-bottom={-1} />
    <directionalLight position={[-3, 2.5, -2.5]} intensity={1.4} color="#7FE3D8" />
    <directionalLight position={[-2, 1.5, 3]} intensity={0.6} color="#FFE2B0" />
    <mesh rotation-x={-Math.PI / 2} receiveShadow><circleGeometry args={[2.2, 64]} /><shadowMaterial opacity={0.28} /></mesh>
    <mesh rotation-x={-Math.PI / 2} position-y={-0.002}><ringGeometry args={[0.55, 0.58, 96]} /><meshBasicMaterial color="#17A2A2" transparent opacity={0.45} /></mesh>
  </>
);

export const CharacterViewer = ({ url, clip, view, zoom, autoRotate, onReady, onError, prop }) => (
  <Canvas shadows dpr={[1, 2]} camera={{ position: [0, 0.95, 3.6], fov: 32 }} gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping, outputColorSpace: THREE.SRGBColorSpace, preserveDrawingBuffer: true }} data-testid="character-viewer-canvas">
    <Studio />
    <Model url={url} clip={clip} onReady={onReady} onError={onError} prop={prop} />
    <Rig view={view} zoom={zoom} autoRotate={autoRotate} />
  </Canvas>
);
